import { useAccountStore } from '~/store/account'
import { isSupportedCurrency } from '~/utils/currency'
import buffer from 'buffer'

const REFRESH_THRESHOLD = 5 * 60 * 1000
// Increase dev timeout to 30s to prevent unnecessary retries that slow down development
const DEFAULT_TIMEOUT = process.dev ? 30000 : 30000

async function getTokens(baseUrl, contextId) {
    const res = await $fetch(`${baseUrl}/api/user/v1/auth/sign-in`, {
        method: 'POST',
        body: JSON.stringify({}),
        headers: {
            'X-TRIPENING-CONTEXT-ID': contextId,
        }
    })
    if (res['access_token']) {
        return res
    }
}

async function refreshTokens(authCookies, baseUrl, contextId) {
    const res = await $fetch(`${baseUrl}/api/user/v1/auth/refresh-token`, {
        method: 'POST',
        body: JSON.stringify({ refresh_token: authCookies.refresh_token }),
        headers: {
            'X-TRIPENING-CONTEXT-ID': contextId,
        }
    })
    if (res['access_token']) {
        return res
    }
}

function getJwtPayload(accessToken) {
    try {
        const userData = accessToken.split('.')
        return JSON.parse(buffer.Buffer.from(userData[1], 'base64').toString())
    } catch (e) {
        console.error('Invalid JWT payload', e)
    }
}

async function getAccessToken(authCookies, baseUrl, contextId) {
    try {
        const accessToken = authCookies?.['access_token']
        const jwtPayload = accessToken ? getJwtPayload(accessToken) : null
        if (accessToken && jwtPayload) {
            if (jwtPayload && jwtPayload.exp * 1000 > (Date.now() + REFRESH_THRESHOLD)) { // 5 min before expiring
                return authCookies
            } else {
                return await refreshTokens(authCookies, baseUrl, contextId)
            }
        } else {
            return await getTokens(baseUrl, contextId)
        }
    } catch (e) {
        console.error('Failed to get tokens', e);
    }
}

export default () => {
    // Capture composable values during setup (Nuxt context is available here).
    // These must NOT be called inside async callbacks like onRequest,
    // because after an await the Nuxt instance context is gone during SSR.
    const config = useRuntimeConfig()
    const accountStore = useAccountStore()
    const authCookie = useCookie('auth')
    const currencyCookie = useCookie('currency')
    const ppcTokenCookie = useCookie('_tripening_ppc')

    return $fetch.create({
        // Add default timeout of 30 seconds
        timeout: DEFAULT_TIMEOUT,

        async onRequest({ request, options }) {
            // Set default timeout if not specified
            if (!options.timeout) {
                options.timeout = DEFAULT_TIMEOUT;
            }

            if (config.public.baseURL) {
                options.baseURL = config.public.baseURL
            }

            if (!options.headers) {
                options.headers = {}
            }

            // Defensive fallback: if the context id somehow isn't set yet (e.g. an
            // SSR context race during plugin init), mint one here rather than sending
            // a request with no X-TRIPENING-CONTEXT-ID header — the API rejects those
            // with a 400, which blanks the whole page (hotel name + images).
            if (!accountStore.contextId) {
                accountStore.setContextId(
                    'xxxxyxxxyxxxyxxxyxxxyxxxyxxxyxxx'.replace(/[xy]/g, (c) => {
                        const r = (Math.random() * 16) | 0
                        return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16)
                    })
                )
                console.error('Context ID not found; generated a fallback')
            }
            options.headers.set('X-TRIPENING-CONTEXT-ID', accountStore.contextId)

            const tokens = await getAccessToken(authCookie.value, config.public.baseURL, accountStore.contextId)
            // Only persist refreshed tokens to cookie on the client.
            // During SSR, the await above loses the Nuxt request context, so
            // writing to useCookie's ref triggers [nuxt] instance unavailable.
            if (import.meta.client) {
                authCookie.value = tokens
            }
            if (tokens?.['access_token']) {
                options.headers.set('Authorization', `Bearer ${tokens['access_token']}`)
            }

            // Never send a currency the API can't accept — an unsupported X-TRIPENING-CURRENCY is
            // rejected with a 400 and would break the whole page. Coerce anything unknown to USD.
            const currency = isSupportedCurrency(currencyCookie.value) ? currencyCookie.value : 'USD'
            options.headers.set('X-TRIPENING-CURRENCY', currency)

            // Signed PPC campaign token (from ptok landing param) — ETL applies the
            // campaign's channel/margin rules when present; absent for organic traffic.
            if (ppcTokenCookie.value) {
                options.headers.set('X-PPC-Campaign', ppcTokenCookie.value)
            }
        },
        async onResponse(res) {
            if (res.response.status === 401) {
                // Handle unauthorized
            }
        },

        async onResponseError({ request, response, options }) {
            // 401 Unauthorized is expected for unauthenticated users - don't log it
            if (response.status === 401) {
                return;
            }

            // Handle 504 Gateway Timeout errors
            if (response.status === 504) {
                console.error('Gateway timeout error:', response.statusText);
            }

            // Handle other server errors
            if (response.status >= 500) {
                console.error('Server error:', response.status, response.statusText);
            }

            // Safety net for a currency the backend doesn't (yet) accept — e.g. the frontend ships
            // SGD support before the API does. Drop the bad session currency back to USD so the next
            // request recovers instead of every call 400ing.
            if (response.status === 400) {
                const reason = response?._data?.errors?.[0]?.detail || response?.statusText || ''
                if (/X-TRIPENING-CURRENCY/i.test(reason) && import.meta.client) {
                    if (currencyCookie.value && currencyCookie.value !== 'USD') {
                        console.warn('Currency rejected by API, falling back to USD:', currencyCookie.value)
                        currencyCookie.value = 'USD'
                    }
                }
            }
        },

        async onRequestError({ request, options, error }) {
            // Handle timeout errors
            if (error.name === 'AbortError' || error.message.includes('timeout')) {
                console.error('Request timeout:', error.message);
            }
            console.error('Request error:', error);
        }
    })
}
